<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('report_sick_days.title') }}
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('report_sick_days.list') }}
        </h5>


        <Search
            v-if="canDoAction"
            :filters="filters"
            :loading="isSearching"
            :userDropdown="userDropdown"
            @search="getReportedSickDays"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getReportedSickDays"/>

<!--        <div class="row align-items-center justify-content-end mt-3"  v-if="canDoAction">-->
<!--          <button type="button" class="btn btn-outline-primary" @click="handleFormReportSickDay(null)">-->
<!--            {{ $t('report_sick_days.create') }}-->
<!--          </button>-->
<!--        </div>-->

        <div class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Mitarbeiternr</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Start</th>
              <th>Ende</th>
              <th class="text-center">eAU</th>
              <th class="text-end">Anzahl der Tage</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(sickDay) in reportedSickDays.data" :key="sickDay.id">
              <td>{{ sickDay?.user?.employeeNumber }}</td>
              <td>{{ sickDay?.user?.firstName }}</td>
              <td>{{ sickDay?.user?.lastName }}</td>
              <td>{{ sickDay?.startDate }}</td>
              <td>{{ sickDay?.endDate }}</td>
              <td class="text-center">{{ sickDay?.eAu === 1 ? 'ja' : 'nein' }}</td>
              <td class="text-end">{{ sickDay?.numberOfDays }}</td>
              <td class="text-end">
                <div v-if="piniaCurrentUser?.id">
                  <button class="btn btn-danger me-3"
                          v-if="canDoAction"
                          @click.stop="handleDeleteSickDay(sickDay?.id)">
                    <i class="os-icon os-icon-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="reportedSickDays?.pagination?.total_pages > 1"
            :totalPages="reportedSickDays?.pagination?.total_pages"
            :currentPage="reportedSickDays?.pagination?.current_page"
            @pagechanged="getReportedSickDays"/>
      </div>
    </div>

    <Modal
        v-model="showModalForm"
        :title="$t('report_sick_days.create')"
        :hide-cancel="true"
        :button-label="$t('general.button.save')"
        sizeModal="modal-xl"
        :hide-footer="true"
        classFooter="justify-content-center">
      <Form :userDropdown="userDropdown"
            v-model:submitForm="submitForm"/>
    </Modal>

    <ModalDelete
        v-model="showModalDelete"
        :header="t('general.delete_item_header')"
        :onDelete="confirmDeleteSickDay">
    </ModalDelete>

  </PageTemplate>
</template>


<script setup>
import reportSickDaysService from '@/api/ReportSickDays';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { getUsersForHrOverviewDropdown } from '@/shared/dropdowns';
import { piniaUserStore } from '@/store/piniaUserStore';
import Form from '@/views/App/ReportSickDays/V1/Form.vue';
import Search from '@/views/App/ReportSickDays/V1/Search.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const piniaUserState   = piniaUserStore();
const piniaCurrentUser = computed(() => piniaUserState.currentUser.user);

const { t }   = useI18n();
const filters = ref({
  userId: '',
  startDate: '',
  endDate: '',
  thisWeek: false,
});

const reportedSickDays   = ref([]);
const reportedSickDaysId = ref(null);
const userDropdown       = ref([]);
const isSearching        = ref(false);
const showModalForm      = ref(false);
const showModalDelete    = ref(false);
const isLoaded           = ref(false);
const submitForm         = ref(false);

watch(submitForm, () => {
  showModalForm.value      = false;
  showModalDelete.value    = false;
  reportedSickDaysId.value = null;
  getReportedSickDays();
});

const allowedRoles = [ 'ROLE_ADMIN', 'ROLE_PERSONAL', 'ROLE_HEAD_OF_HR' ];

const canDoAction = computed(() =>
    allowedRoles.some(role => piniaCurrentUser.value.roles.includes(role)),
);

async function getReportedSickDays(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = reportedSickDays.value?.pagination?.items_per_page;

  try {
    const { data } = await reportSickDaysService.getReportedSickDays({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    reportedSickDays.value = data;
    isSearching.value      = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function handleFormReportSickDay(id) {
  CloseToast();
  reportedSickDaysId.value = id;
  showModalDelete.value    = false;
  showModalForm.value      = true;
}

function handleDeleteSickDay(id) {
  CloseToast();
  reportedSickDaysId.value = id;
  showModalDelete.value    = true;
  showModalForm.value      = false;
}

async function confirmDeleteSickDay() {
  try {
    await reportSickDaysService.deleteReportedSickDays(reportedSickDaysId.value);

    filters.value.userId    = '';
    filters.value.startDate = '';
    filters.value.endDate   = '';
    filters.value.thisWeek  = false;
    showModalDelete.value   = false;

    await getReportedSickDays();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getReportedSickDays(),
    getUsersForHrOverviewDropdown(userDropdown),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>